import React from 'react';
import { motion } from 'framer-motion';
import { useCRM } from '../context/CRMContext';
import { format, differenceInDays, isAfter } from 'date-fns';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { NotificationService } from '../services/NotificationService';

const {
  FiRefreshCw, FiCalendar, FiAlertTriangle, FiCheckCircle, FiMail
} = FiIcons;

const Renewals = () => {
  const { contracts, clients, settings } = useCRM();

  const activeContracts = contracts.filter(c => c.status === 'active');
  const categorizedContracts = activeContracts.reduce(
    (acc, contract) => {
      const endDate = new Date(contract.endDate);
      const today = new Date();
      const daysUntilExpiry = differenceInDays(endDate, today);

      if (daysUntilExpiry < 0) {
        acc.expired.push(contract);
      } else if (daysUntilExpiry <= settings.reminderDays) {
        acc.upcoming.push(contract);
      } else {
        acc.future.push(contract);
      }
      return acc;
    },
    { expired: [], upcoming: [], future: [] }
  );

  const handleSendReminder = (contract) => {
    const client = clients.find(c => c.id === contract.clientId);
    NotificationService.sendContractRenewalReminder(contract, client);
  };

  const ContractCard = ({ contract, category }) => {
    const client = clients.find(c => c.id === contract.clientId);
    const endDate = new Date(contract.endDate);
    const daysUntilExpiry = differenceInDays(endDate, new Date());

    const getCategoryStyle = () => {
      switch (category) {
        case 'expired': return 'border-l-4 border-red-500 bg-red-50';
        case 'upcoming': return 'border-l-4 border-yellow-500 bg-yellow-50';
        case 'future': return 'border-l-4 border-green-500 bg-green-50';
        default: return '';
      }
    };

    const getStatusIcon = () => {
      switch (category) {
        case 'expired': return <SafeIcon icon={FiAlertTriangle} className="h-5 w-5 text-red-600" />;
        case 'upcoming': return <SafeIcon icon={FiCalendar} className="h-5 w-5 text-yellow-600" />;
        case 'future': return <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-600" />;
        default: return null;
      }
    };

    const getStatusText = () => {
      if (category === 'expired') {
        return `Expired ${Math.abs(daysUntilExpiry)} days ago`;
      } else if (category === 'upcoming') {
        return `${daysUntilExpiry} days remaining`;
      } else {
        return `${daysUntilExpiry} days remaining`;
      }
    };

    return (
      <Card className={`p-6 ${getCategoryStyle()}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3">
            {getStatusIcon()}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{contract.title}</h3>
              <p className="text-sm text-gray-600">{client?.name} - {client?.company}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{format(endDate, 'MMM dd, yyyy')}</p>
            <p className={`text-xs ${category === 'expired' ? 'text-red-600' : category === 'upcoming' ? 'text-yellow-600' : 'text-green-600'}`}>
              {getStatusText()}
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {contract.value && (
            <p className="text-sm text-gray-600"><span className="font-medium">Value:</span> ${parseFloat(contract.value).toLocaleString()}</p>
          )}
          <p className="text-sm text-gray-600 line-clamp-2"><span className="font-medium">Scope:</span> {contract.projectScope}</p>
        </div>
        {(category === 'expired' || category === 'upcoming') && client?.email && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button size="sm" variant="outline" onClick={() => handleSendReminder(contract)} className="w-full">
              <SafeIcon icon={FiMail} className="h-4 w-4 mr-2" /> Send Renewal Reminder
            </Button>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Contract Renewals</h1>
        <div className="text-sm text-gray-500">Reminder threshold: {settings.reminderDays} days</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 border-l-4 border-red-500"><div className="flex items-center"><SafeIcon icon={FiAlertTriangle} className="h-8 w-8 text-red-600" /><div className="ml-4"><p className="text-sm font-medium text-gray-600">Expired Contracts</p><p className="text-2xl font-bold text-red-600">{categorizedContracts.expired.length}</p></div></div></Card>
        <Card className="p-6 border-l-4 border-yellow-500"><div className="flex items-center"><SafeIcon icon={FiCalendar} className="h-8 w-8 text-yellow-600" /><div className="ml-4"><p className="text-sm font-medium text-gray-600">Upcoming Renewals</p><p className="text-2xl font-bold text-yellow-600">{categorizedContracts.upcoming.length}</p></div></div></Card>
        <Card className="p-6 border-l-4 border-green-500"><div className="flex items-center"><SafeIcon icon={FiCheckCircle} className="h-8 w-8 text-green-600" /><div className="ml-4"><p className="text-sm font-medium text-gray-600">Future Renewals</p><p className="text-2xl font-bold text-green-600">{categorizedContracts.future.length}</p></div></div></Card>
      </div>

      {categorizedContracts.expired.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center"><SafeIcon icon={FiAlertTriangle} className="h-5 w-5 text-red-600 mr-2" /> Expired Contracts</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {categorizedContracts.expired.map((contract, index) => (
              <motion.div key={contract.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                <ContractCard contract={contract} category="expired" />
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {categorizedContracts.upcoming.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center"><SafeIcon icon={FiCalendar} className="h-5 w-5 text-yellow-600 mr-2" /> Upcoming Renewals (Next {settings.reminderDays} days)</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {categorizedContracts.upcoming.map((contract, index) => (
              <motion.div key={contract.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                <ContractCard contract={contract} category="upcoming" />
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {categorizedContracts.future.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center"><SafeIcon icon={FiRefreshCw} className="h-5 w-5 text-green-600 mr-2" /> Future Renewals</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {categorizedContracts.future.slice(0, 6).map((contract, index) => (
              <motion.div key={contract.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                <ContractCard contract={contract} category="future" />
              </motion.div>
            ))}
          </div>
          {categorizedContracts.future.length > 6 && (
            <div className="text-center mt-4"><p className="text-gray-500">And {categorizedContracts.future.length - 6} more future renewals...</p></div>
          )}
        </div>
      )}

      {activeContracts.length === 0 && (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiRefreshCw} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No active contracts</h3>
          <p className="text-gray-500">Add some contracts to track their renewal dates.</p>
        </Card>
      )}
    </div>
  );
};

export default Renewals;